// src/pages/MemberManage/index.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Select,
  Button,
  Avatar,
  Tag,
  Divider,
  Flex,
  Tabs,
  TabList,
  Tab,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import ReusableSearchBar from "../../components/ReusableSearchBar";
import { AccountCreateModal } from "../../components/Modals/AccountCreateModal";
import { AdvertiserMemberModal } from "../../components/Modals/AdvertiserMember";
import { CreatorMemberModal } from "../../components/Modals/CreatorMember";
import { ScheduleManageModal } from "../../components/Modals/ScheduleManageModal";
import { RunLogsModal } from "../../components/Modals/RunLogsModal";
import InstagramProfilesTab from "./InstagramProfilesTab";
import { accountList, updateUserStatus } from "../../../src/api";
import { useNavigate } from "react-router-dom";

// 숫자 포맷터
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "-");

// 타입별 UI 메타
const typeMeta = {
  creator: { label: "크리에이터", color: "#E9D8FD", text: "#553C9A" },
  advertiser: { label: "광고주", color: "#BEE3F8", text: "#1E4E8C" },
  agency: { label: "에이전시", color: "#CDE4FF", text: "#1E40AF" },
};

const statusMeta = {
  ACTIVE: { label: "활성", color: "green" },
  INACTIVE: { label: "비활성", color: "gray" },
  BANNED: { label: "차단", color: "red" },
};

// API → UI 표준화 매핑 함수
const mapCreator = (item) => ({
  id: item.profileId,
  userId: item.userId,
  type: "creator",
  name: item.displayName,
  userStatus: item.userStatus ?? "ACTIVE",
  profileImg: "",
  follower: item.socialFollowersValue ?? 0,
  posts: item.socialPostsValue ?? 0,
  avgViews: item.socialAvgViewsValue ?? 0,
  missions: item.completedMissionCount ?? 0,
  achievementRate: item.avgAchievementsRate ?? 0,
});

const mapAdvertiser = (item) => ({
  id: item.profileId,
  userId: item.userId,
  type: "advertiser",
  name: item.displayName,
  userStatus: item.userStatus ?? "ACTIVE",
  profileImg: "",
  CEO: item.representativeName ?? "-",
  businessRegistered: item.verified ?? false,
  category: item.organizationBizTypeCategory ?? "-",
  onGoingCampaignCount: item.onGoingCampaignCount ?? 0,
  completedCampaignCount: item.completedCampaignCount ?? 0,
});

// admin → agency UI 데이터로 매핑
const mapAdmin = (item) => ({
  id: item.profileId,
  userId: item.userId,
  type: "agency",
  name: item.displayName,
  userStatus: item.userStatus ?? "ACTIVE",
  profileImg: "",
  CEO: "-",
  businessRegistered: false,
  category: "-",
  missions: 0,
  achievementRate: 0,
});

export default function MemberPage() {
  const cardBg = useColorModeValue("white", "gray.800");
  const navigate = useNavigate();
  const toast = useToast();

  const [creatorData, setCreatorData] = useState([]);
  const [advertiserData, setAdvertiserData] = useState([]);
  const [agencyData, setAgencyData] = useState([]); // admin → agency 데이터
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadAccounts = async () => {
    try {
      const [creatorRes, advertiserRes, adminRes] = await Promise.all([
        accountList({ role: "creator", page: 1, size: 100, status: statusFilter }),
        accountList({ role: "advertiser", page: 1, size: 100, status: statusFilter }),
        accountList({ role: "admin", page: 1, size: 100, status: statusFilter }),
      ]);

      setCreatorData(creatorRes.data.data.contents?.map(mapCreator) ?? []);
      setAdvertiserData(
        advertiserRes.data.data.contents?.map(mapAdvertiser) ?? []
      );
      setAgencyData(adminRes.data.data.contents?.map(mapAdmin) ?? []);
    } catch (e) {
      // 정보 못 받으면 재로그인
      navigate(`/login`);
      console.error("계정 조회 실패:", e);
    }
  };

  // ── 검색 상태
  const [keyword, setKeyword] = useState("");
  const [instagramKeyword, setInstagramKeyword] = useState("");

  // 탭 상태: creator | advertiser | agency | instagram
  const [roleTab, setRoleTab] = useState("creator");

  // 정렬 상태
  const [baseSortKey, setBaseSortKey] = useState("follower");
  const [baseSortDir, setBaseSortDir] = useState("asc");

  const [actSortKey, setActSortKey] = useState("missions");
  const [actSortDir, setActSortDir] = useState("asc");

  // 계정 생성 모달
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  // 스케줄 관리 / 수집로그 모달 (인스타그램 탭용)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [runLogsModalOpen, setRunLogsModalOpen] = useState(false);

  // UI에 표시되는 데이터 (인스타그램 프로필 탭은 별도 컴포넌트)
  const rows = useMemo(() => {
    const data =
      roleTab === "creator"
        ? creatorData
        : roleTab === "advertiser"
        ? advertiserData
        : agencyData;

    return (
      [...data]
        .filter((u) =>
          keyword ? u.name?.toLowerCase().includes(keyword.toLowerCase()) : true
        )
        // 활동 정렬
        .sort((a, b) => {
          const A = a[actSortKey] ?? 0;
          const B = b[actSortKey] ?? 0;
          return actSortDir === "asc" ? A - B : B - A;
        })
        // Creator만 기본정보 정렬
        .sort((a, b) => {
          if (roleTab !== "creator") return 0;
          const A = a[baseSortKey] ?? 0;
          const B = b[baseSortKey] ?? 0;
          return baseSortDir === "asc" ? A - B : B - A;
        })
    );
  }, [
    roleTab,
    keyword,
    actSortKey,
    actSortDir,
    baseSortKey,
    baseSortDir,
    creatorData,
    advertiserData,
    agencyData,
  ]);

  const isInstagramTab = roleTab === "instagram";

  const handleStatusChange = async (user, status) => {
    if (!user?.userId || !status || user.userStatus === status) return;
    const ok = window.confirm(`${user.name ?? "계정"} 상태를 ${statusMeta[status]?.label ?? status}(으)로 변경할까요?`);
    if (!ok) return;
    try {
      await updateUserStatus({ userId: user.userId, status });
      toast({ status: "success", title: "계정 상태가 변경되었습니다." });
      await loadAccounts();
    } catch (e) {
      toast({
        status: "error",
        title: "계정 상태 변경 실패",
        description: e?.response?.data?.message ?? "잠시 후 다시 시도해주세요.",
      });
    }
  };

  const tabMeta = {
    creator: {
      title: "가입된 크리에이터 목록입니다.",
      description: "캠페인에 참여한 크리에이터 계정을 조회하고 관리합니다.",
    },
    advertiser: {
      title: "가입된 광고주 목록입니다.",
      description: "캠페인을 등록한 광고주 계정을 조회하고 관리합니다.",
    },
    agency: {
      title: "가입된 에이전시 목록입니다.",
      description: "광고주와 크리에이터를 연결하는 에이전시 계정을 조회하고 관리합니다.",
    },
    instagram: {
      title: "크롤링된 인스타그램 프로필 목록입니다.",
      description: "태그를 선택하면 해당 카테고리의 프로필만 필터링됩니다.",
    },
  };

  const currentMeta = tabMeta[roleTab] ?? tabMeta.creator;

  return (
    <Box>
      {/* 제목 + 검색 (항상 고정 위치) */}
      <Flex justify="space-between" align="center" mb="24px">
        <Box>
          <Text fontSize="xl" fontWeight="bold" mb="2">
            {currentMeta.title}
          </Text>
          <Text color="gray.600">
            {currentMeta.description}
          </Text>
        </Box>
        <ReusableSearchBar
          key={roleTab}
          placeholder={isInstagramTab ? "username 검색" : "멤버 검색"}
          width="276px"
          onSearch={isInstagramTab ? setInstagramKeyword : setKeyword}
        />
      </Flex>

      {/* 정렬바 (항상 표시, 인스타그램 탭일 때 비활성화) */}
      <Flex
        align="center"
        justify="space-between"
        mb="16px"
        wrap="wrap"
        gap="12px"
      >
        <HStack spacing="12px">
          {/* 기본정보 정렬 - creator만 */}
          <Select
            bg="white"
            w="120px"
            value={baseSortKey}
            onChange={(e) => setBaseSortKey(e.target.value)}
            isDisabled={isInstagramTab || roleTab !== "creator"}
          >
            <option value="follower">팔로워 순</option>
            <option value="posts">게시물 순</option>
            <option value="avgViews">조회수 순</option>
          </Select>

          <Button
            onClick={() =>
              setBaseSortDir(baseSortDir === "asc" ? "desc" : "asc")
            }
            bg="white"
            border="1px solid #E2E8F0"
            borderRadius="8px"
            isDisabled={isInstagramTab || roleTab !== "creator"}
            w="80px"
            h="40px"
          >
            <HStack spacing="6px">
              <Text fontWeight={500}>정렬</Text>
              {baseSortDir === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </HStack>
          </Button>

          {/* 활동 정렬 */}
          <Select
            bg="white"
            w="120px"
            value={actSortKey}
            onChange={(e) => setActSortKey(e.target.value)}
            isDisabled={isInstagramTab}
          >
            <option value="missions">미션 수</option>
            <option value="achievementRate">달성률</option>
          </Select>

          <Button
            onClick={() => setActSortDir(actSortDir === "asc" ? "desc" : "asc")}
            bg="white"
            border="1px solid #E2E8F0"
            borderRadius="8px"
            h="40px"
            isDisabled={isInstagramTab}
          >
            <HStack spacing="6px">
              <Text fontWeight={500}>정렬</Text>
              {actSortDir === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </HStack>
          </Button>

          {!isInstagramTab && (
            <Select
              bg="white"
              w="130px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ACTIVE">활성</option>
              <option value="INACTIVE">비활성</option>
              <option value="BANNED">차단</option>
              <option value="ALL">전체</option>
            </Select>
          )}
        </HStack>

        {isInstagramTab ? (
          <HStack gap="8px">
            <Button
              w="120px"
              h="44px"
              fontWeight={700}
              fontSize="16px"
              bgColor="#0054C0"
              color="white"
              borderRadius="15px"
              onClick={() => setScheduleModalOpen(true)}
            >
              스케줄 관리
            </Button>
            <Button
              w="100px"
              h="44px"
              fontWeight={700}
              fontSize="16px"
              bgColor="gray.600"
              color="white"
              borderRadius="15px"
              onClick={() => setRunLogsModalOpen(true)}
            >
              수집 로그
            </Button>
          </HStack>
        ) : (
          <Button
            w="130px"
            h="44px"
            fontWeight={700}
            fontSize="16px"
            bgColor="#0054C0"
            color="white"
            borderRadius="15px"
            onClick={openModal}
          >
            계정 생성
          </Button>
        )}
      </Flex>

      {/* 역할 탭 */}
      <Tabs
        index={["creator", "advertiser", "agency", "instagram"].indexOf(roleTab)}
        onChange={(i) =>
          setRoleTab(["creator", "advertiser", "agency", "instagram"][i])
        }
        colorScheme="blue"
        mb="12px"
      >
        <TabList>
          <Tab fontSize="14px" fontWeight={500}>
            크리에이터
          </Tab>
          <Tab fontSize="14px" fontWeight={500}>
            광고주
          </Tab>
          <Tab fontSize="14px" fontWeight={500}>
            에이전시
          </Tab>
          <Tab fontSize="14px" fontWeight={500}>
            인스타그램 프로필
          </Tab>
        </TabList>
      </Tabs>

      {/* 인스타그램 프로필 탭: 별도 컴포넌트 */}
      {roleTab === "instagram" ? (
        <InstagramProfilesTab keyword={instagramKeyword} />
      ) : (
        <>
      {/* 테이블 */}
      <Box bg={cardBg} borderTopRadius="20px" boxShadow="sm" overflow="hidden">
        {/* 헤더 */}
        <Flex
          py="12px"
          bg="#E2EEFF"
          fontWeight="600"
          color="gray.700"
          fontSize="sm"
        >
          <Box textAlign="center" w="15%">
            구분
          </Box>
          <Box w="25%">프로필</Box>
          <Box w="25%">기본 정보</Box>
          <Box w="25%">활동</Box>
          <Box w="10%" textAlign="center">상태</Box>
        </Flex>
        <Divider m="0" />

        {/* 로우 */}
        {rows.map((u, idx) => (
          <Box key={u.id} overflowY="auto">
            <Flex
              py="16px"
              h="105px"
              alignItems="center"
              _hover={{ bg: "gray.50" }}
              onClick={() => {
                setSelectedAccountId(u.id);
                setSelectedType(u.type);
              }}
            >
              {/* 구분 */}
              <Box w="15%" display="flex" justifyContent="center">
                <Tag
                  bg={typeMeta[u.type]?.color}
                  color={typeMeta[u.type]?.text}
                  fontWeight="700"
                  px="10px"
                  py="6px"
                  borderRadius="md"
                >
                  {typeMeta[u.type]?.label}
                </Tag>
              </Box>

              {/* 프로필 */}
              <Box w="25%">
                <HStack>
                  <Avatar name={u.name} src={u.profileImg} size="sm" />
                  <VStack align="start" spacing="2px">
                    <Text fontWeight="700" fontSize="20px">
                      {u.name}
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* 기본 정보 */}
              <Box w="25%">
                {u.type === "creator" ? (
                  <VStack align="start">
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        팔로워
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {fmt(u.follower)}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        게시물
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {fmt(u.posts)}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        평균 조회수
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {fmt(u.avgViews)}
                      </Text>
                    </HStack>
                  </VStack>
                ) : (
                  <VStack align="start">
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        대표자명
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {u.CEO}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        사업자 인증 상태
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {u.businessRegistered ? "O" : "X"}
                      </Text>
                    </HStack>
                    <HStack w="95%" alignItems="center">
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        업종/산업
                      </Text>
                      <Text
                        fontWeight={600}
                        fontSize="16px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        flex="1"
                      >
                        {u.category}
                      </Text>
                    </HStack>
                  </VStack>
                )}
              </Box>

              {/* 활동 */}
              <Box w="25%">
                {u.type === "creator" ? (
                  <VStack align="start">
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        완료한 미션 수
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {fmt(u.missions)}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        평균 목표 달성률
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {u?.achievementRate}%
                      </Text>
                    </HStack>
                  </VStack>
                ) : (
                  <VStack align="start">
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        진행중인 캠페인
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {u?.onGoingCampaignCount ?? 0}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                        완료한 캠페인
                      </Text>
                      <Text fontWeight={600} fontSize="16px">
                        {u?.completedCampaignCount ?? 0}
                      </Text>
                    </HStack>
                  </VStack>
                )}
              </Box>

              <Box w="10%" display="flex" justifyContent="center" onClick={(e) => e.stopPropagation()}>
                <VStack spacing="6px">
                  <Tag colorScheme={statusMeta[u.userStatus]?.color ?? "gray"} fontWeight="700">
                    {statusMeta[u.userStatus]?.label ?? u.userStatus}
                  </Tag>
                  <Select
                    size="xs"
                    value={u.userStatus}
                    onChange={(e) => handleStatusChange(u, e.target.value)}
                    w="82px"
                  >
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="BANNED">차단</option>
                  </Select>
                </VStack>
              </Box>
            </Flex>

            {idx < rows.length - 1 && <Divider m="0" />}
          </Box>
        ))}

        {rows.length === 0 && (
          <Box p="40px" textAlign="center" color="gray.500">
            검색/필터 조건에 맞는 멤버가 없습니다.
          </Box>
        )}
      </Box>
        </>
      )}

      {/* 계정 생성 모달 */}
      <AccountCreateModal
        isOpen={isOpen}
        onClose={closeModal}
        onSuccess={() => loadAccounts()}
      />

      {/* 멤버 상세 모달 */}
      {selectedAccountId && selectedType === "creator" && (
        <CreatorMemberModal
          accountId={selectedAccountId}
          isOpen={!!selectedAccountId}
          onClose={() => setSelectedAccountId(null)}
          onSuccess={() => loadAccounts()}
        />
      )}

      {selectedAccountId && selectedType === "advertiser" && (
        <AdvertiserMemberModal
          accountId={selectedAccountId}
          isOpen={!!selectedAccountId}
          type={selectedType}
          onClose={() => setSelectedAccountId(null)}
          onSuccess={() => loadAccounts()}
        />
      )}

      <ScheduleManageModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
      />

      <RunLogsModal
        isOpen={runLogsModalOpen}
        onClose={() => setRunLogsModalOpen(false)}
      />
    </Box>
  );
}
